import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, Save, X, Check } from "lucide-react";
import api, { formatErr } from "../../lib/api";

const empty = { name:"", brand:"", category:"luxury", price:0, original_price:0, description:"", images:[], stock:1, sku:"", gender:"unisex", movement:"", case_size:"", case_material:"", strap:"", water_resistance:"", featured:false, is_new:false, sections:[], low_stock_threshold:3 };

const SECTIONS = [
  { id: "vintage_luxury", label: "Vintage & Luxury Watches" },
  { id: "sport_chronograph", label: "Sport & Chronograph" },
  { id: "new_arrivals", label: "New Arrivals" },
  { id: "on_sale", label: "On Sale" },
];

export default function ShopAddProduct() {
  const [form, setForm] = useState({...empty});
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [uploadedB64, setUploadedB64] = useState("");
  const [variations, setVariations] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [usage, setUsage] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(()=>{ api.get("/brands").then(r=>setBrands(r.data)); api.get("/categories").then(r=>setCats(r.data)); refreshUsage(); }, []);
  const refreshUsage = () => api.get("/ai/usage/me").then(r=>setUsage(r.data));

  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result;
      setUploadedB64(b64);
      // Auto-add original to images list
      setForm(s => ({...s, images: [b64]}));
    };
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    if (!uploadedB64) return toast.error("Upload an image first");
    setGenerating(true); setVariations([]);
    try {
      const r = await api.post("/ai/generate-variations", { image_base64: uploadedB64, count: 3 });
      if (r.data.ok && r.data.images?.length) {
        setVariations(r.data.images.map((im, i) => ({
          ...im, id: i,
          dataUrl: im.url ? im.url : `data:${im.mime || "image/png"};base64,${im.data}`,
        })));
        toast.success(`${r.data.images.length} variations generated via ${r.data.provider || "Gemini"}`);
      } else {
        toast.error(r.data.error || "AI did not return any images");
      }
      refreshUsage();
    } catch (e) { toast.error(formatErr(e)); } finally { setGenerating(false); }
  };

  const toggleSection = (id) => setForm((s) => ({
    ...s,
    sections: s.sections.includes(id) ? s.sections.filter((x) => x !== id) : [...s.sections, id],
  }));

  const toggleVariation = (v) => {
    setForm(s => ({...s, images: s.images.includes(v.dataUrl) ? s.images.filter(x=>x!==v.dataUrl) : [...s.images, v.dataUrl]}));
  };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Name and price are required");
    if (form.images.length === 0) return toast.error("At least one image required");
    setSaving(true);
    try {
      await api.post("/products", {
        ...form,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        stock: parseInt(form.stock),
        low_stock_threshold: parseInt(form.low_stock_threshold),
      });
      toast.success("Submitted — awaiting admin approval");
      setForm({...empty}); setUploadedB64(""); setVariations([]);
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div><div className="overline mb-2">Catalog</div><h1 className="font-display text-4xl text-white">Add Product with AI</h1></div>
        <div className="text-xs text-white/50">AI today: <span className="text-copper">{usage.today||0}/{usage.daily_limit||0}</span> · month: <span className="text-copper">{usage.month||0}/{usage.monthly_limit||0}</span></div>
      </div>

      <div className="grid md:grid-cols-[1fr_1fr] gap-6">
        {/* Upload + AI */}
        <div className="card-luxe p-6 space-y-5">
          <div className="overline">1. Upload Image</div>
          <label data-testid="upload-image" className="block aspect-square bg-[#070707] border border-dashed border-white/15 hover:border-copper cursor-pointer flex items-center justify-center overflow-hidden">
            {uploadedB64 ? <img src={uploadedB64} alt="" className="w-full h-full object-cover"/> : <div className="text-center text-white/40"><Upload size={32} className="mx-auto mb-2"/><div className="text-xs uppercase tracking-luxe">Click to upload</div></div>}
            <input type="file" accept="image/*" className="hidden" onChange={handleFile}/>
          </label>
          <button data-testid="ai-generate" disabled={!uploadedB64 || generating || usage.paused} onClick={generate} className="btn-primary w-full justify-center">
            <Sparkles size={14}/> {generating? "Generating with Gemini Nano Banana…" : "Generate AI Variations"}
          </button>
          {usage.paused && <div className="text-xs text-red-400">AI access paused by Developer.</div>}

          {variations.length > 0 && (
            <div>
              <div className="overline mb-2">2. Pick AI Variations</div>
              <div className="grid grid-cols-3 gap-2">
                {variations.map(v => {
                  const sel = form.images.includes(v.dataUrl);
                  return (
                    <button key={v.id} data-testid={`var-${v.id}`} onClick={()=>toggleVariation(v)} className={`relative aspect-square overflow-hidden border ${sel? "border-copper" : "border-white/10"}`}>
                      <img src={v.dataUrl} alt="" className="w-full h-full object-cover"/>
                      {sel && <div className="absolute top-1 right-1 bg-copper text-black p-1"><Check size={11}/></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="overline mb-2">Selected Images ({form.images.length})</div>
            <div className="grid grid-cols-4 gap-2">
              {form.images.map((u, i) => (
                <div key={i} className="relative aspect-square">
                  <img src={u} alt="" className="w-full h-full object-cover"/>
                  <button onClick={()=>setForm({...form, images: form.images.filter((_,j)=>j!==i)})} className="absolute top-1 right-1 bg-red-500/80 text-white p-1"><X size={10}/></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product details */}
        <div className="card-luxe p-6 space-y-3">
          <div className="overline">3. Product Details</div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="p-name" placeholder="Name" className="input-box col-span-2" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}/>
            <input data-testid="ai-brand" type="text" placeholder="Brand (e.g. Rolex) *" className="input-box" value={form.brand} list="shop-ai-brand-suggestions" onChange={(e)=>setForm({...form, brand:e.target.value})}/><datalist id="shop-ai-brand-suggestions">{brands.map(b=><option key={b.id} value={b.name}/>)}</datalist>
            <select className="input-box" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})}>{cats.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select>
            <input data-testid="p-price" type="number" placeholder="Price (AED)" className="input-box" value={form.price} onChange={(e)=>setForm({...form, price:e.target.value})}/>
            <input type="number" placeholder="Stock" className="input-box" value={form.stock} onChange={(e)=>setForm({...form, stock:e.target.value})}/>
            <input placeholder="SKU" className="input-box" value={form.sku} onChange={(e)=>setForm({...form, sku:e.target.value})}/>
            <select className="input-box" value={form.gender} onChange={(e)=>setForm({...form, gender:e.target.value})}><option>men</option><option>women</option><option>unisex</option></select>
            <input placeholder="Movement" className="input-box" value={form.movement} onChange={(e)=>setForm({...form, movement:e.target.value})}/>
            <input placeholder="Case Size" className="input-box" value={form.case_size} onChange={(e)=>setForm({...form, case_size:e.target.value})}/>
            <input placeholder="Case Material" className="input-box" value={form.case_material} onChange={(e)=>setForm({...form, case_material:e.target.value})}/>
            <input placeholder="Strap" className="input-box" value={form.strap} onChange={(e)=>setForm({...form, strap:e.target.value})}/>
            <textarea rows={3} placeholder="Description" className="input-box col-span-2 resize-none" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})}/>
          </div>

          {/* Storefront sections */}
          <div className="mt-3">
            <div className="overline mb-2">Storefront Sections</div>
            <div className="text-[11px] text-white/55 mb-2">Pick where this product should appear on the home page (multi-select).</div>
            <div className="flex flex-wrap gap-2 mb-2">
              {SECTIONS.map((sec) => {
                const selected = form.sections.includes(sec.id);
                return (
                  <button
                    key={sec.id}
                    type="button"
                    data-testid={`section-${sec.id}`}
                    onClick={() => toggleSection(sec.id)}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-luxe border transition-colors"
                    style={selected ? { backgroundColor: "#B89766", color: "#fff", borderColor: "#B89766" } : { backgroundColor: "transparent", color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.15)" }}
                  >
                    {sec.label}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 text-[11px] text-white/65">
              <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })}/> Tag as “New” (auto-shown in New Arrivals)
            </label>
          </div>

          <button data-testid="p-save" disabled={saving} onClick={save} className="btn-primary w-full justify-center mt-3"><Save size={14}/> {saving? "Submitting…":"Submit for Approval"}</button>
        </div>
      </div>
    </div>
  );
}
